import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import {
    MsgVO,
    NoteRequestAPI,
    ReservationVO,
    QuoteVO,
    OrderVO,
    TaskVO,
    DueTaskVO,
    GuestContactVO,
    AttachmentVO,
    MOCK_MESSAGES,
    MOCK_CONTACT
} from './unified-inbox.models';

@Injectable({ providedIn: 'root' })
export class UnifiedInboxService {

    private msgsUrl = `${environment.apiUrl}/msgs`;
    private reservationsByContactUrl = `${environment.apiUrl}/reservations/by-contact`;
    private quotesByContactUrl = `${environment.apiUrl}/quotes/by-contact`;
    private ordersByContactUrl = `${environment.apiUrl}/order/by-contact`;
    private tasksByContactUrl = `${environment.apiUrl}/tasks/by-contact`;

    constructor(private http: HttpClient) {}

    /**
     * GET /api/2.6/msgs/unified — conversation list.
     * Returns one row per conversation (newest first) for the unified inbox left column.
     */
    listConversations(filters: {
        context?: number;
        limit?: number;
        before?: string;
        folder?: string;
    }): Observable<ApiResponse> {
        let params = new HttpParams();
        if (filters.context != null) { params = params.set('context', String(filters.context)); }
        if (filters.limit != null) { params = params.set('limit', String(filters.limit)); }
        if (filters.before) { params = params.set('before', filters.before); }
        if (filters.folder) { params = params.set('folder', filters.folder); }
        return this.http.get<ApiResponse>(`${this.msgsUrl}/unified`, { params });
    }

    /** @deprecated use listConversations(). Kept for any existing references. */
    listMessages(filters: {
        context: number;
        conversationId?: string;
        mailbox?: string;
        channel?: string;
        label?: string;
    }): Observable<ApiResponse> {
        let params = new HttpParams().set('context', String(filters.context));
        if (filters.conversationId) { params = params.set('conversationId', filters.conversationId); }
        if (filters.mailbox) { params = params.set('mailbox', filters.mailbox); }
        if (filters.channel) { params = params.set('channel', filters.channel); }
        if (filters.label) { params = params.set('label', filters.label); }
        return this.http.get<ApiResponse>(`${this.msgsUrl}/unified`, { params });
    }

    getThread(conversationId: string): Observable<ApiResponse> {
        // TODO: wire to GET /api/2.6/msgs/unified/thread/:conversationId when backend ships
        return this.http.get<ApiResponse>(`${this.msgsUrl}/unified/thread/${conversationId}`);
    }

    postNote(req: NoteRequestAPI): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${this.msgsUrl}/note`, req);
    }

    sendEmail(payload: {
        conversationId: string;
        msgContext: number;
        toEmail: string;
        subject: string;
        body: string;
        templateId?: number;
    }): Observable<ApiResponse> {
        const body = {
            conversationId: payload.conversationId,
            msgContext: payload.msgContext,
            mediums: ['EMAIL'],
            subject: payload.subject,
            emailText: payload.body,
            msgRecipients: [{ email: payload.toEmail, emailRecipientType: 'TO' }],
            templateId: payload.templateId || null
        };
        return this.http.post<ApiResponse>(`${this.msgsUrl}/v2/reply-mail`, body);
    }

    sendSms(payload: {
        conversationId: string;
        msgContext: number;
        toMobile: string;
        body: string;
    }): Observable<ApiResponse> {
        const body = {
            conversationId: payload.conversationId,
            toMobile: payload.toMobile,
            body: payload.body,
            msgContext: payload.msgContext
        };
        return this.http.post<ApiResponse>(`${this.msgsUrl}/sms/send`, body);
    }

    getReservationsByContact(emailAddress?: string, mobileNum?: string): Observable<ApiResponse> {
        let params = new HttpParams();
        if (emailAddress) { params = params.set('emailAddress', emailAddress); }
        if (mobileNum) { params = params.set('mobileNum', mobileNum); }
        return this.http.get<ApiResponse>(this.reservationsByContactUrl, { params });
    }

    getQuotesByContact(emailAddress?: string, mobileNum?: string): Observable<ApiResponse> {
        let params = new HttpParams();
        if (emailAddress) { params = params.set('emailAddress', emailAddress); }
        if (mobileNum) { params = params.set('mobileNum', mobileNum); }
        return this.http.get<ApiResponse>(this.quotesByContactUrl, { params });
    }

    getOrdersByContact(emailAddress?: string, mobileNum?: string): Observable<ApiResponse> {
        let params = new HttpParams();
        if (emailAddress) { params = params.set('emailAddress', emailAddress); }
        if (mobileNum) { params = params.set('mobileNum', mobileNum); }
        return this.http.get<ApiResponse>(this.ordersByContactUrl, { params });
    }

    getTasksByContact(
        emailAddress?: string,
        mobileNum?: string,
        reservationId?: number,
        guestMasterProfileId?: number
    ): Observable<ApiResponse> {
        let params = new HttpParams();
        if (emailAddress) { params = params.set('emailAddress', emailAddress); }
        if (mobileNum) { params = params.set('mobileNum', mobileNum); }
        if (reservationId != null) { params = params.set('reservationId', String(reservationId)); }
        if (guestMasterProfileId != null) { params = params.set('guestMasterProfileId', String(guestMasterProfileId)); }
        return this.http.get<ApiResponse>(this.tasksByContactUrl, { params });
    }

    getGuestSummary(email: string, name?: string): Observable<ApiResponse> {
        let params = new HttpParams().set('email', email);
        if (name) { params = params.set('name', name); }
        return this.http.get<ApiResponse>(`${this.msgsUrl}/guest-summary`, { params });
    }

    getTemplates(context: number): Observable<ApiResponse> {
        const params = new HttpParams().set('context', String(context));
        return this.http.get<ApiResponse>(`${environment.apiUrl}/message-templates`, { params });
    }

    summarizeThread(conversationId: string): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(
            `${this.msgsUrl}/unified/thread/${encodeURIComponent(conversationId)}/summarize`,
            {}
        );
    }

    suggestReply(conversationId: string, replyChannel: 'EMAIL' | 'SMS'): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(
            `${this.msgsUrl}/unified/thread/${encodeURIComponent(conversationId)}/suggest-reply`,
            { replyChannel }
        );
    }

    markConversationRead(conversationId: string): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(
            `${this.msgsUrl}/unified/conversation/${encodeURIComponent(conversationId)}/read`,
            {}
        );
    }

    getConversationLabels(conversationId: string): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(
            `${this.msgsUrl}/unified/labels`,
            { params: new HttpParams().set('conversationId', conversationId) }
        );
    }

    toggleConversationLabel(conversationId: string, label: string): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${this.msgsUrl}/unified/label`, { conversationId, label });
    }

    // =========================================================================
    // UMS label definitions — backed by task_tag (tagType=10)
    // =========================================================================

    /** GET all UMS label definitions from the tag system (tagType=10). */
    getLabelDefinitions(): Observable<ApiResponse> {
        const params = new HttpParams()
            .set('tagType', '10')
            .set('page', '0')
            .set('size', '200');
        return this.http.get<ApiResponse>(`${environment.apiUrl}/tags`, { params });
    }

    /** POST a new UMS label definition to the tag system (tagType=10). */
    createLabelDefinition(name: string, color?: string): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${environment.apiUrl}/tags`, {
            name,
            tagType: 10,
            tagGroupCategory: 'UMS',
            tagCategoryColor: color || '#888888',
            visibility: 2
        });
    }

    /** DELETE a UMS label definition from the tag system. */
    deleteLabelDefinition(tagId: number): Observable<ApiResponse> {
        return this.http.delete<ApiResponse>(`${environment.apiUrl}/tags/${tagId}`);
    }

    getAttachments(msgId: number): Observable<ApiResponse> {
        return this.http.get<ApiResponse>(`${this.msgsUrl}/v2/${msgId}/attachments`);
    }

    searchMessages(query: string, context?: number): Observable<ApiResponse> {
        let params = new HttpParams().set('q', query);
        if (context != null) { params = params.set('context', String(context)); }
        return this.http.get<ApiResponse>(`${this.msgsUrl}/search`, { params });
    }

    /** Fetch the full indexed document from OpenSearch for a single msgId. */
    getMsgDocument(msgId: number): Observable<any> {
        const params = new HttpParams().set('msgId', String(msgId));
        return this.http.get<any>(`${this.msgsUrl}/opensearch/document`, { params });
    }

    /**
     * GET /api/2.6/msgs/unified/due-tasks
     * Returns tasks due for follow-up today (and overdue tasks) for the given user.
     */
    getDueTasks(userId?: number, context?: number): Observable<ApiResponse> {
        let params = new HttpParams();
        if (userId != null) { params = params.set('userId', String(userId)); }
        if (context != null) { params = params.set('context', String(context)); }
        return this.http.get<ApiResponse>(`${this.msgsUrl}/unified/due-tasks`, { params });
    }

    archiveConversation(conversationId: string): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${this.msgsUrl}/unified/archive`, { conversationId });
    }

    sendToBizDev(conversationId: string): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${this.msgsUrl}/unified/send-to-context`, {
            conversationId,
            targetContext: 9
        });
    }

    bulkArchive(conversationIds: string[]): Observable<ApiResponse> {
        return this.http.post<ApiResponse>(`${this.msgsUrl}/unified/bulk-archive`, { conversationIds });
    }

    getMockMessages(): MsgVO[] {
        return MOCK_MESSAGES;
    }

    getMockContact(): GuestContactVO {
        return MOCK_CONTACT;
    }
}
